import { ref, reactive, watch, nextTick } from 'vue'
import { getStorageSync, setStorageSync, removeStorageSync, showToast, getStorageInfoSync } from '@tarojs/taro'

/**
 * 通用暂存功能
 * @param {Object} options 配置选项
 * @param {string} options.storageKey 存储的key名称（会自动添加前缀）
 * @param {Object} options.formData 表单数据的ref
 * @param {Array} options.includeFields 需要缓存的字段，不传则全部缓存
 * @param {Array} options.excludeFields 排除缓存的字段，默认排除一些系统字段
 * @param {Function} options.beforeSave 保存前的钩子函数
 * @param {Function} options.beforeRestore 恢复前的钩子函数
 * @param {Function} options.onRestore 恢复缓存后的回调
 * @param {Function} options.getDetailData 获取详情数据的函数
 * @param {boolean} options.enableAutoSave 是否开启实时缓存，默认false
 * @param {number} options.autoSaveDelay 实时缓存防抖延迟时间（毫秒），默认1000ms
 * @param {boolean} options.showAutoSaveToast 实时缓存时是否显示提示，默认false
 * @param {number} options.maxStorageCount 同类型最大存储条数，默认3条
 * @param {string|Function} options.dynamicKey 动态key生成函数或字符串，用于区分新增/编辑模式
 * @returns {Object} 返回操作方法
 */
export function useTempStorage(options = {}) {
  const {
    storageKey,
    formData,
    includeFields = null,
    excludeFields = ['recordId', 'medicationId', 'baseId', 'baseName'],
    beforeSave = null,
    beforeRestore = null,
    onRestore = null,
    getDetailData = null,
    enableAutoSave = false,
    autoSaveDelay = 1000,
    showAutoSaveToast = false,
    maxStorageCount = 3, // 新增：同类型最大存储条数
    dynamicKey = null // 新增：动态key生成函数
  } = options

  if (!storageKey) {
    throw new Error('storageKey is required')
  }

  if (!formData || !formData.value) {
    throw new Error('formData ref is required')
  }

  const STORAGE_PREFIX = 'TEMP_STORAGE_'

  // 生成动态存储key
  const generateStorageKey = () => {
    let finalKey = storageKey

    if (dynamicKey) {
      if (typeof dynamicKey === 'function') {
        const dynamicPart = dynamicKey()
        finalKey = dynamicPart ? `${storageKey}_${dynamicPart}` : `${storageKey}_add`
      } else if (typeof dynamicKey === 'string') {
        finalKey = `${storageKey}_${dynamicKey}`
      }
    }

    return finalKey
  }

  // 获取当前的最终存储key
  let finalStorageKey = `${STORAGE_PREFIX}${generateStorageKey()}`

  // 获取同类型的所有存储key（纯Taro实现）
  const getSameTypeStorageKeys = () => {
    try {
      const sameTypeKeys = []
      const basePattern = `${STORAGE_PREFIX}${storageKey}`

      console.log(`🔍 开始查找同类型存储keys，匹配模式: ${basePattern}*`)

      try {
        // 使用Taro API获取所有storage keys
        const info = getStorageInfoSync()
        const allKeys = info.keys || []

        console.log(`📊 Taro.getStorageInfoSync 获取到 ${allKeys.length} 个keys:`, allKeys)

        if (allKeys.length > 0) {
          allKeys.forEach(key => {
            if (key.startsWith(basePattern)) {
              sameTypeKeys.push(key)
              console.log(`✅ 找到同类型key: ${key}`)
            }
          })
          console.log(`🎯 通过API成功找到 ${sameTypeKeys.length} 个同类型keys`)
        } else {
          console.warn('⚠️ getStorageInfoSync返回的keys数组为空')
          // 🔧 针对用户真实环境的优化降级方案
          console.log('🔄 启用用户环境优化降级方案')

          const possibleSuffixes = ['_add', '_view', '_copy', '_detail']

          // 添加编辑模式的可能性（短ID）
          for (let i = 1; i <= 100; i++) {
            possibleSuffixes.push(`_edit_${i}`)
          }



          // 尝试基础模式
          console.log(`🔍 尝试 ${possibleSuffixes.length} 个基础模式...`)
          possibleSuffixes.forEach(suffix => {
            const testKey = `${basePattern}${suffix}`
            try {
              const data = getStorageSync(testKey)
              if (data && (data.data || data.timestamp)) {
                sameTypeKeys.push(testKey)
                console.log(`✅ 基础模式找到: ${testKey}`)
              }
            } catch (e) {
              // 忽略不存在的key
            }
          })

          console.log(`🎯 优化降级方案总计找到 ${sameTypeKeys.length} 个同类型keys`)
        }
      } catch (apiError) {
        console.error('❌ Taro.getStorageInfoSync 调用失败:', apiError)
        console.log('🔄 降级到遍历方案')

        // 完全降级到遍历方案
        const possibleSuffixes = ['_add', '_view', '_copy', '_detail']
        for (let i = 1; i <= 50; i++) {
          possibleSuffixes.push(`_edit_${i}`)
        }

        possibleSuffixes.forEach(suffix => {
          const testKey = `${basePattern}${suffix}`
          try {
            const data = getStorageSync(testKey)
            if (data && (data.data || data.timestamp)) {
              sameTypeKeys.push(testKey)
              console.log(`✅ 降级遍历找到: ${testKey}`)
            }
          } catch (e) {
            // 忽略错误
          }
        })
      }

      console.log(`📋 最终结果: 发现 ${sameTypeKeys.length} 个${storageKey}类型的存储数据`)
      return sameTypeKeys

    } catch (error) {
      console.error('❌ 获取同类型存储key完全失败:', error)
      return []
    }
  }

  // 清理同类型的旧存储数据
  const cleanOldSameTypeStorage = () => {
    try {
      const sameTypeKeys = getSameTypeStorageKeys()
      console.log(`🔍 清理检查: 发现${storageKey}类型的存储数据:`, sameTypeKeys.length, '条, 限制:', maxStorageCount, '条')

      // 当数据条数大于等于限制时需要清理，为新数据腾出空间
      if (sameTypeKeys.length >= maxStorageCount) {
        console.log(`⚠️ 需要清理！当前: ${sameTypeKeys.length}条, 限制: ${maxStorageCount}条，开始清理...`)

        // 获取每个key的时间戳信息
        const keyInfos = sameTypeKeys.map(key => {
          try {
            const data = getStorageSync(key)
            return {
              key,
              timestamp: data && data.timestamp ? data.timestamp : 0,
              storageKey: data && data.storageKey ? data.storageKey : key.replace(`${STORAGE_PREFIX}`, '')
            }
          } catch (e) {
            return { key, timestamp: 0, storageKey: 'unknown' }
          }
        }).filter(info => info.timestamp > 0)

        // 按时间戳排序，最旧的在前面
        keyInfos.sort((a, b) => a.timestamp - b.timestamp)

        console.log('📋 按时间排序（最旧到最新）:')
        keyInfos.forEach((info, index) => {
          const date = new Date(info.timestamp)
          console.log(`  ${index + 1}. ${info.storageKey} - ${date.toLocaleString()}`)
        })

        // 计算需要删除的数量 - 保留最新的 (maxStorageCount-1) 条，为新数据腾出1个位置
        const needToRemove = sameTypeKeys.length - (maxStorageCount - 1)
        const keysToRemove = keyInfos.slice(0, needToRemove)

        console.log(`🗑️ 将删除最旧的 ${keysToRemove.length} 条数据，为新数据预留空间:`)
        keysToRemove.forEach((info, index) => {
          const date = new Date(info.timestamp)
          console.log(`  ${index + 1}. ${info.storageKey} - ${date.toLocaleString()}`)
        })

        keysToRemove.forEach(info => {
          try {
            removeStorageSync(info.key)
            console.log(`✅ 已清理旧的${storageKey}暂存数据:`, info.storageKey)
          } catch (error) {
            console.warn(`❌ 清理失败:`, info.key, error)
          }
        })

        if (keysToRemove.length > 0) {
          showToast({
            title: `已清理${keysToRemove.length}条旧暂存`,
            icon: 'none',
            duration: 2000
          })
          console.log(`🎉 清理完成，删除了${keysToRemove.length}条最旧数据，为新数据预留空间`)
        }
      } else {
        console.log(`✅ 数据量在限制内 (${sameTypeKeys.length}/${maxStorageCount})，无需清理`)
      }
    } catch (error) {
      console.error('❌ 清理同类型旧存储失败:', error)
    }
  }

  // 是否有缓存数据的标识
  const hasCacheData = ref(false)

  // 实时缓存相关状态
  const isAutoSaveEnabled = ref(enableAutoSave)
  const autoSaveTimer = ref(null)
  const isInitializing = ref(false) // 标记是否正在初始化，避免初始化时触发自动保存

  // 深拷贝函数
  const deepClone = (obj) => {
    if (obj === null || typeof obj !== 'object') return obj
    if (obj instanceof Date) return new Date(obj.getTime())
    if (obj instanceof Array) return obj.map(item => deepClone(item))
    if (typeof obj === 'object') {
      const clonedObj = {}
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          clonedObj[key] = deepClone(obj[key])
        }
      }
      return clonedObj
    }
  }

  // blob转base64的工具函数
  const blobToBase64 = (blobUrl) => {
    return new Promise((resolve, reject) => {
      fetch(blobUrl)
        .then(response => response.blob())
        .then(blob => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result)
          reader.onerror = reject
          reader.readAsDataURL(blob)
        })
        .catch(reject)
    })
  }

  // base64转blob的工具函数
  const base64ToBlob = (base64Data) => {
    try {
      const arr = base64Data.split(',')
      const mime = arr[0].match(/:(.*?);/)[1]
      const byteCharacters = atob(arr[1])
      const byteNumbers = new Array(byteCharacters.length)

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }

      const byteArray = new Uint8Array(byteNumbers)
      return new Blob([byteArray], { type: mime })
    } catch (error) {
      console.error('base64转blob失败:', error)
      return null
    }
  }

  // 过滤需要缓存的字段
  const filterFields = async (data) => {
    let result = {}

    if (includeFields && includeFields.length > 0) {
      // 如果指定了包含字段，只缓存这些字段
      includeFields.forEach(field => {
        if (data.hasOwnProperty(field)) {
          result[field] = deepClone(data[field])
        }
      })
    } else {
      // 如果没有指定包含字段，缓存所有字段（除了排除的）
      result = deepClone(data)
      excludeFields.forEach(field => {
        delete result[field]
      })
    }

    // 特殊处理图片字段 - 将blob链接转换为base64
    // 🔧 修复：同时处理 uploadFiles 和 tempUploadFiles 字段
    const imageFields = ['uploadFiles', 'tempUploadFiles']

    for (const fieldName of imageFields) {
      if (result[fieldName] && Array.isArray(result[fieldName])) {
        try {
          result[fieldName] = await Promise.all(
            result[fieldName].map(async (file) => {
              // 检查是否有blob链接需要转换
              // 优先检查 url 字段，然后检查 taroFilePath 字段，最后检查 filePath 字段
              const blobUrl = file.url || file.taroFilePath || file.filePath;

              if (blobUrl && blobUrl.startsWith('blob:')) {
                try {
                  console.log(`正在转换${fieldName}字段的blob链接:`, blobUrl);
                  const base64 = await blobToBase64(blobUrl)
                  return {
                    ...file,
                    url: base64, // 将url字段替换为base64
                    originalBlobUrl: blobUrl, // 保存原始blob链接
                    originalFilePath: file.filePath, // 保存原始filePath
                    originalAccessUrl: file.accessUrl, // 保存原始accessUrl
                    isBase64: true // 标记为base64数据
                  }
                } catch (error) {
                  console.warn(`${fieldName}字段图片转换失败:`, error, file);
                  return file
                }
              }
              return file
            })
          )
          console.log(`${fieldName}字段图片转换完成，转换后的数据:`, result[fieldName]);
        } catch (error) {
          console.error(`批量转换${fieldName}字段图片失败:`, error)
        }
      }
    }

    // 🔧 修复：如果没有tempUploadFiles字段但有uploadFiles字段，复制一份到tempUploadFiles
    if (!result.tempUploadFiles && result.uploadFiles && Array.isArray(result.uploadFiles)) {
      result.tempUploadFiles = [...result.uploadFiles];
      console.log('复制uploadFiles到tempUploadFiles字段');
    }

    return result
  }

  // 保存缓存
  const saveToStorage = async (isAutoSave = false) => {
    try {
      let dataToSave = await filterFields(formData.value) // 改为await调用

      // 执行保存前钩子
      if (beforeSave && typeof beforeSave === 'function') {
        dataToSave = beforeSave(dataToSave) || dataToSave
      }

      const storageData = {
        data: dataToSave,
        timestamp: Date.now(),
        version: '1.0',
        storageType: storageKey, // 添加存储类型标识
        storageKey: generateStorageKey() // 保存完整的存储key
      }

      // 重新生成当前的存储key
      finalStorageKey = `${STORAGE_PREFIX}${generateStorageKey()}`

      // 先检查并清理超出限制的旧数据，为新数据腾出空间
      cleanOldSameTypeStorage()

      // 然后保存新数据
      setStorageSync(finalStorageKey, storageData)
      hasCacheData.value = true
      console.log(`数据已${isAutoSave ? '自动' : '手动'}暂存到: ${finalStorageKey}`, dataToSave)

      // 根据是否为自动保存决定是否显示提示
      if (isAutoSave && showAutoSaveToast) {
        showToast({
          title: '自动暂存',
          icon: 'none',
          duration: 1000
        })
      } else if (!isAutoSave) {
        showToast({
          title: '暂存成功',
          icon: 'success',
          duration: 1500
        })
      }

      return true
    } catch (error) {
      console.error('暂存失败:', error)
      if (!isAutoSave) {
        showToast({
          title: '暂存失败',
          icon: 'error',
          duration: 2000
        })
      }
      return false
    }
  }

  // 获取缓存数据
  const getFromStorage = () => {
    try {
      // 重新生成当前的存储key，以防动态key发生变化
      finalStorageKey = `${STORAGE_PREFIX}${generateStorageKey()}`
      const storageData = getStorageSync(finalStorageKey)
      return storageData && storageData.data ? storageData : null
    } catch (error) {
      console.error('获取缓存失败:', error)
      return null
    }
  }

  // 清除缓存
  const clearStorage = () => {
    try {
      // 重新生成当前的存储key
      finalStorageKey = `${STORAGE_PREFIX}${generateStorageKey()}`
      removeStorageSync(finalStorageKey)
      hasCacheData.value = false
      // 清除自动保存定时器
      if (autoSaveTimer.value) {
        clearTimeout(autoSaveTimer.value)
        autoSaveTimer.value = null
      }
      console.log(`缓存已清除: ${finalStorageKey}`)
      return true
    } catch (error) {
      console.error('清除缓存失败:', error)
      return false
    }
  }

  // 合并数据：缓存数据覆盖详情数据
  const mergeData = (detailData, cacheData) => {
    const mergedData = deepClone(detailData)

    // 缓存数据覆盖详情数据，缓存中没有的保持详情的数据
    Object.keys(cacheData).forEach(key => {
      if (cacheData[key] !== undefined && cacheData[key] !== null) {
        mergedData[key] = cacheData[key]
      }
    })

    return mergedData
  }

  // 恢复缓存数据
  const restoreFromStorage = async (detailData = null) => {
    try {
      const cachedData = getFromStorage()

      if (cachedData && cachedData.data) {
        let dataToRestore = cachedData.data

        // 🔧 修复：同时处理 uploadFiles 和 tempUploadFiles 字段的base64图片恢复
        const imageFields = ['uploadFiles', 'tempUploadFiles']

        for (const fieldName of imageFields) {
          if (dataToRestore[fieldName] && Array.isArray(dataToRestore[fieldName])) {
            dataToRestore[fieldName] = dataToRestore[fieldName].map(file => {
              if (file.isBase64 && file.url) {
                // 将base64转回blob链接用于显示
                console.log(`正在恢复${fieldName}字段的base64图片:`, file);
                const blob = base64ToBlob(file.url)
                if (blob) {
                  const blobUrl = URL.createObjectURL(blob)

                  return {
                    ...file,
                    url: blobUrl, // 恢复url字段为新的blob链接
                    taroFilePath: blobUrl, // 同时更新taroFilePath字段
                    isBase64: false, // 重置标记
                    // 保留原有的 filePath 等重要字段
                    filePath: file.filePath || file.originalFilePath,
                    accessUrl: file.accessUrl || file.originalAccessUrl
                  }
                }
              }
              return file
            })
            console.log(`${fieldName}字段图片恢复完成，恢复后的数据:`, dataToRestore[fieldName]);
          }
        }

        // 🔧 修复：如果没有tempUploadFiles字段但有uploadFiles字段，复制一份到tempUploadFiles
        if (!dataToRestore.tempUploadFiles && dataToRestore.uploadFiles && Array.isArray(dataToRestore.uploadFiles)) {
          dataToRestore.tempUploadFiles = [...dataToRestore.uploadFiles];
          console.log('恢复时复制uploadFiles到tempUploadFiles字段');
        }

        // 如果有详情数据，进行合并
        if (detailData) {
          dataToRestore = mergeData(detailData, dataToRestore)
        }

        // 执行恢复前钩子
        if (beforeRestore && typeof beforeRestore === 'function') {
          dataToRestore = beforeRestore(dataToRestore, detailData, cachedData.data) || dataToRestore
        }

        // 恢复数据到formData
        Object.keys(dataToRestore).forEach(key => {
          if (formData.value.hasOwnProperty(key)) {
            formData.value[key] = dataToRestore[key]
          }
        })

        hasCacheData.value = true

        // 执行恢复后回调
        if (onRestore && typeof onRestore === 'function') {
          await nextTick()
          onRestore(dataToRestore, detailData, cachedData.data)
        }

        console.log('缓存数据已恢复:', dataToRestore)
        return dataToRestore
      } else {
        hasCacheData.value = false
        return null
      }
    } catch (error) {
      console.error('恢复缓存失败:', error)
      hasCacheData.value = false
      return null
    }
  }  // 自动保存功能（防抖）
  const triggerAutoSave = () => {
    if (!isAutoSaveEnabled.value || isInitializing.value) return

    // 清除之前的定时器
    if (autoSaveTimer.value) {
      clearTimeout(autoSaveTimer.value)
    }

    // 设置新的定时器（异步保存）
    autoSaveTimer.value = setTimeout(async () => {
      await saveToStorage(true) // 传入true表示这是自动保存
    }, autoSaveDelay)
  }

  // 开启实时缓存
  const startAutoSave = () => {
    isAutoSaveEnabled.value = true
    console.log('实时缓存已开启')
  }

  // 关闭实时缓存
  const stopAutoSave = () => {
    isAutoSaveEnabled.value = false
    if (autoSaveTimer.value) {
      clearTimeout(autoSaveTimer.value)
      autoSaveTimer.value = null
    }
    console.log('实时缓存已关闭')
  }

  // 监听表单数据变化进行自动保存
  let watchStop = null
  if (enableAutoSave) {
    watchStop = watch(
      () => formData.value,
      () => {
        triggerAutoSave()
      },
      {
        deep: true, // 深度监听对象变化
        flush: 'post' // 在DOM更新后执行
      }
    )
  }

  // 初始化：在页面进入时调用
  const initialize = async (shouldGetDetail = true) => {
    try {
      isInitializing.value = true // 设置初始化标记

      const cachedData = getFromStorage()
      let detailData = null

      // 如果有获取详情的函数且需要获取详情
      if (getDetailData && typeof getDetailData === 'function' && shouldGetDetail) {
        try {
          detailData = await getDetailData()
        } catch (error) {
          console.error('获取详情数据失败:', error)
        }
      }

      // 如果有缓存数据
      if (cachedData && cachedData.data) {
        await restoreFromStorage(detailData)

        // 初始化完成后，延迟一段时间再启用自动保存监听，避免恢复数据时触发保存
        setTimeout(() => {
          isInitializing.value = false
        }, 100)

        return { hasCache: true, cacheData: cachedData.data, detailData }
      } else if (detailData) {
        // 如果没有缓存但有详情数据，直接使用详情数据
        Object.keys(detailData).forEach(key => {
          if (formData.value.hasOwnProperty(key)) {
            formData.value[key] = detailData[key]
          }
        })

        setTimeout(() => {
          isInitializing.value = false
        }, 100)

        return { hasCache: false, cacheData: null, detailData }
      }

      isInitializing.value = false
      return { hasCache: false, cacheData: null, detailData: null }
    } catch (error) {
      console.error('初始化失败:', error)
      isInitializing.value = false
      return { hasCache: false, cacheData: null, detailData: null }
    }
  }

  // 重置：清除缓存并重置表单
  const reset = (resetData = {}) => {
    isInitializing.value = true // 重置时暂时停止自动保存

    clearStorage()

    // 重置formData到初始状态或指定状态
    Object.keys(formData.value).forEach(key => {
      if (resetData.hasOwnProperty(key)) {
        formData.value[key] = resetData[key]
      } else {
        // 根据字段类型设置默认值
        const value = formData.value[key]
        if (Array.isArray(value)) {
          formData.value[key] = []
        } else if (typeof value === 'string') {
          formData.value[key] = ''
        } else if (typeof value === 'number') {
          formData.value[key] = 0
        } else if (typeof value === 'boolean') {
          formData.value[key] = false
        } else {
          formData.value[key] = null
        }
      }
    })

    setTimeout(() => {
      isInitializing.value = false
    }, 100)

    console.log('数据已重置')
  }

  // 提交完成：清除缓存
  const onSubmitSuccess = () => {
    clearStorage()
    console.log('提交成功，缓存已清除')
  }

  // 组件卸载时的清理函数
  const destroy = () => {
    if (watchStop) {
      watchStop()
    }
    if (autoSaveTimer.value) {
      clearTimeout(autoSaveTimer.value)
    }
    console.log('暂存功能已销毁 3333')
  }

  // 检查是否有缓存
  const checkHasCache = () => {
    // 重新生成当前的存储key
    finalStorageKey = `${STORAGE_PREFIX}${generateStorageKey()}`
    const cachedData = getFromStorage()
    hasCacheData.value = !!(cachedData && cachedData.data)
    return hasCacheData.value
  }

  // 清理所有同类型的暂存数据（新增方法）
  const clearAllSameTypeStorage = () => {
    try {
      const sameTypeKeys = getSameTypeStorageKeys()
      sameTypeKeys.forEach(key => {
        try {
          removeStorageSync(key)
        } catch (error) {
          console.warn(`清理失败: ${key}`, error)
        }
      })

      hasCacheData.value = false
      console.log(`已清理所有${storageKey}类型的暂存数据，共${sameTypeKeys.length}条`)

      if (sameTypeKeys.length > 0) {
        showToast({
          title: `已清理${sameTypeKeys.length}条暂存数据`,
          icon: 'success',
          duration: 2000
        })
      }

      return sameTypeKeys.length
    } catch (error) {
      console.error('清理同类型暂存数据失败:', error)
      return 0
    }
  }

  // 获取同类型存储信息（新增方法）
  const getSameTypeStorageInfo = () => {
    try {
      const sameTypeKeys = getSameTypeStorageKeys()
      const infos = sameTypeKeys.map(key => {
        try {
          const data = getStorageSync(key)
          return {
            key,
            timestamp: data && data.timestamp ? data.timestamp : 0,
            storageKey: data && data.storageKey ? data.storageKey : '',
            hasData: !!(data && data.data)
          }
        } catch (e) {
          return { key, timestamp: 0, storageKey: '', hasData: false }
        }
      }).filter(info => info.hasData)

      // 按时间戳排序
      infos.sort((a, b) => b.timestamp - a.timestamp)

      return {
        count: infos.length,
        maxCount: maxStorageCount,
        items: infos,
        storageType: storageKey
      }
    } catch (error) {
      console.error('获取同类型存储信息失败:', error)
      return {
        count: 0,
        maxCount: maxStorageCount,
        items: [],
        storageType: storageKey
      }
    }
  }

  return {
    // 状态
    hasCacheData,
    isAutoSaveEnabled,

    // 主要方法
    saveToStorage,          // 暂存数据（手动）
    restoreFromStorage,     // 恢复缓存
    clearStorage,           // 清除当前缓存
    initialize,             // 初始化（页面进入时调用）
    reset,                  // 重置
    onSubmitSuccess,        // 提交成功（清除缓存）
    destroy,                // 销毁（组件卸载时调用）

    // 实时缓存控制
    startAutoSave,          // 开启实时缓存
    stopAutoSave,           // 关闭实时缓存
    triggerAutoSave,        // 手动触发自动保存

    // 辅助方法
    checkHasCache,          // 检查是否有缓存
    getFromStorage,         // 获取缓存数据

    // 新增：存储管理方法
    clearAllSameTypeStorage,  // 清理所有同类型的暂存数据
    getSameTypeStorageInfo,   // 获取同类型存储信息
    getSameTypeStorageKeys,   // 获取同类型存储keys
    generateStorageKey,       // 生成存储key

    // 内部方法（一般不需要直接调用）
    mergeData,
    filterFields
  }
}
